import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import InfestSnapNavbarLogo from '../assets/navbar/InfestSnapNavbarLogo.svg';
import profileNavbar from '../assets/navbar/profileNavbar.svg';
import arrow_downNavbar from '../assets/navbar/arrow_downNavbar.svg';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser } from '../store/auth/authSlice';
import { toast } from 'react-toastify';
import { formatClientName } from '../utils/formatNameHelper';
import useClickOutside from '../hooks/useClickOutside';
import { APP_ROUTES } from '../routes/config';

function Navbar() {
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { user, userInfo } = useSelector((s) => s.auth);

  // Determine what to display
  const clientName = formatClientName(userInfo?.client_name);
  const displayLogo = userInfo?.client_logo || InfestSnapNavbarLogo;
  const hasClientBranding =
    Boolean(userInfo?.client_logo) && Boolean(clientName);

  const heading = APP_ROUTES.find((route) => {
    const regex = new RegExp('^' + route.path.replace(/:\w+/g, '[^/]+') + '$');
    return regex.test(location.pathname);
  })?.title;

  const toggleDropdown = () => {
    setIsDropdownVisible((prev) => !prev);
  };

  const handleUserProfile = () => {
    navigate('/user-profile');
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    toast.success('Logout Successful');
  };

  // Close dropdown when clicking outside
  useClickOutside(dropdownRef, () => setIsDropdownVisible(false));

  // close dropdown after navigation too
  useEffect(() => {
    setIsDropdownVisible(false);
  }, [location.pathname]);

  return (
    <div className="flex justify-between items-center pl-[36px] pr-[28px] py-2 shadow-md relative z-50">
      <div className="flex items-center">
        <img
          width={hasClientBranding ? '40px' : '70%'}
          height={hasClientBranding ? '40px' : 'auto'}
          src={displayLogo}
          alt={hasClientBranding ? 'Client Logo' : 'InfestSnapNavbarLogo'}
          className={
            hasClientBranding ? 'object-contain max-w-[120px] max-h-[40px]' : ''
          }
          onError={(e) => {
            // Fallback to default logo if client logo fails to load
            e.target.src = InfestSnapNavbarLogo;
            e.target.style.width = '70%';
            e.target.style.height = 'auto';
            e.target.className = '';
          }}
        />
        {hasClientBranding && (
          <span className="ml-3 font-semibold text-lg text-gray-700">
            {clientName}
          </span>
        )}
      </div>

      {heading && (
        <h1 className="text-lg font-semibold text-gray-800">{heading}</h1>
      )}

      <div
        className="flex items-center relative"
        ref={dropdownRef}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setIsDropdownVisible(false);
        }}
      >
        <img
          width="30"
          src={profileNavbar}
          alt="Toggle user menu"
          className="cursor-pointer"
          onClick={toggleDropdown}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') toggleDropdown();
          }}
          tabIndex={0}
          role="button"
        />
        <button
          type="button"
          className="text-[14px] pl-1 max-w-[200px] truncate cursor-pointer"
          title={user?.user_name || 'User'}
          aria-haspopup="menu"
          aria-expanded={isDropdownVisible}
          onClick={toggleDropdown}
        >
          {user?.user_name || `User`}
        </button>
        <img
          className={`ml-1 cursor-pointer transition-transform duration-200 ease-out ${isDropdownVisible ? '-rotate-180' : 'rotate-0'
            }`}
          src={arrow_downNavbar}
          alt="Toggle user menu"
          onClick={toggleDropdown}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') toggleDropdown();
          }}
          tabIndex={0}
          role="button"
        />

        {isDropdownVisible && (
          <div className="absolute top-[40px] right-0 bg-white shadow-md rounded-md z-[99999] font-semibold min-w-[150px]">
            <button
              className="block px-4 py-2 text-left w-full hover:bg-gray-100"
              onClick={handleUserProfile}
            >
              User Profile
            </button>
            <button
              className="block px-4 py-2 text-left w-full hover:bg-gray-100"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Navbar;
